import React, { useEffect, useRef, useState } from 'react';
import {
  Brain,
  BookOpenText,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Gift,
  Languages,
  MonitorSmartphone,
  Ticket,
  Users,
  Volume2,
  X,
  Zap,
} from 'lucide-react';
import logo from '../images/logo.svg';
import studyLanguageScreenshot from '../images/screenshots/study-language.png';
import nativeLanguageScreenshot from '../images/screenshots/native-language.png';
import manageFlashcardsScreenshot from '../images/screenshots/manage-flashcards.png';
import editFlashcardScreenshot from '../images/screenshots/edit-flashcard.png';
import examplesScreenshot from '../images/screenshots/examples.png';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FREE_FLASHCARD_LIMIT, PREMIUM_PRICE_LABEL } from '../lib/subscription';
import { PLAY_STORE_URL } from '../lib/usePwaInstall';
import { BETA_APPLICATION_MESSAGE, BETA_APPLICATION_SUBJECT } from '../lib/beta';

const APP_SCREENSHOTS = [
  {
    src: studyLanguageScreenshot,
    alt: 'Studying a flashcard in the Laftel Lingo Android app',
    title: 'Study your flashcards',
    description:
      'Listen to the pronunciation, review example sentences, or edit the flashcard on the fly.',
  },
  {
    src: manageFlashcardsScreenshot,
    alt: 'Managing a flashcard deck in the Laftel Lingo Android app',
    title: 'Manage your flashcards',
    description:
      'Add a new word, import vocabulary from your notes, or generate new vocabulary automatically. You can also edit or delete existing flashcards here.',
  },
  {
    src: editFlashcardScreenshot,
    alt: 'Editing a flashcard in the Laftel Lingo Android app',
    title: 'Edit each flashcard',
    description:
      'Update the word, translation, pronunciation, or priority whenever you need to.',
  },
  {
    src: examplesScreenshot,
    alt: 'Viewing flashcard examples in the Laftel Lingo Android app',
    title: 'Learn with real examples',
    description:
      'Every new flashcard automatically includes example sentences to help you learn in context. You can edit them to your liking.',
  },
  {
    src: nativeLanguageScreenshot,
    alt: 'Choosing a native language in the Laftel Lingo Android app',
    title: 'Reveal the meaning',
    description: 'Flip the flashcard to check the word in your native language.',
  },
];

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const lightboxRef = useRef(null);
  const lightboxDialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const wasLightboxOpenRef = useRef(false);

  // Jump the lightbox's scroll-snap carousel straight to the clicked
  // screenshot before it's visible, so opening it never shows a different
  // slide first.
  useEffect(() => {
    if (lightboxIndex !== null && lightboxRef.current) {
      lightboxRef.current.scrollLeft = lightboxIndex * lightboxRef.current.clientWidth;
    }
  }, [lightboxIndex]);

  // Move focus into the dialog on open and back to the thumbnail that opened
  // it on close, so keyboard users never lose their place. Guarded by
  // wasLightboxOpenRef so navigating between screenshots (index changing
  // while still open) doesn't re-steal focus from the close button.
  useEffect(() => {
    const isOpen = lightboxIndex !== null;
    if (isOpen && !wasLightboxOpenRef.current) {
      previouslyFocusedRef.current = document.activeElement;
      closeButtonRef.current?.focus();
    } else if (!isOpen && wasLightboxOpenRef.current) {
      previouslyFocusedRef.current?.focus?.();
      previouslyFocusedRef.current = null;
    }
    wasLightboxOpenRef.current = isOpen;
  }, [lightboxIndex]);

  const moveLightbox = (direction) => {
    const el = lightboxRef.current;
    if (!el) return;
    const currentIndex = Math.round(el.scrollLeft / el.clientWidth);
    const targetIndex = Math.max(0, Math.min(currentIndex + direction, APP_SCREENSHOTS.length - 1));
    el.scrollTo({ left: targetIndex * el.clientWidth, behavior: 'smooth' });
  };

  // The current slide is read straight from scrollLeft rather than mirrored into
  // state, so this can't fight the browser's own scroll-snap animation while a
  // swipe or a previous click-to-navigate is still settling.
  const navigateLightbox = (e) => {
    const el = lightboxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const goingLeft = e.clientX - rect.left < rect.width / 2;
    moveLightbox(goingLeft ? -1 : 1);
  };

  const handleLightboxKeyDown = (e) => {
    if (e.key === 'Escape') {
      setLightboxIndex(null);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      moveLightbox(e.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (e.key === 'Tab') {
      const dialog = lightboxDialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Signed-in visitors land on their dashboard instead of the marketing page,
  // in a regular browser tab or an installed PWA alike. Gated on auth
  // resolving so the marketing page never flashes on screen before the
  // redirect fires.
  if (loading) return null;
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div aria-hidden={lightboxIndex !== null ? true : undefined}>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <img src={logo} alt="Laftel Lingo logo" className="mx-auto w-28 h-28 sm:w-40 sm:h-40" />
            <h1 className="text-4xl sm:text-6xl font-bold text-foreground mb-5 max-w-4xl mx-auto leading-tight">
              Turn language notes into <span className="text-primary">vocabulary flashcards</span>
            </h1>
            <p className="text-base sm:text-xl text-muted-foreground mb-4 max-w-3xl mx-auto">
              Paste in the words you&rsquo;re learning and Lingo builds the flashcards for you, with
              translations, example sentences, and pronunciation. Spaced repetition brings each word
              back right before you&rsquo;d forget it, so new vocabulary actually sticks.
            </p>
            <p className="text-base sm:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Add a new word in <span className="text-primary">2</span> seconds. Turn your notes into
              flashcards in <span className="text-primary">2</span> clicks.
            </p>
          </div>

          <div className="flex justify-center gap-4 mb-20">
            {!isAuthenticated ? (
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <img
                  src="/icons/GetItOnGooglePlay_Badge_Web_color_English.svg"
                  alt="Get it on Google Play"
                  className="h-14 sm:h-16"
                />
              </a>
            ) : (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-primary text-primary-foreground px-6 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
              >
                Go to Dashboard
              </button>
            )}
          </div>

          <div className="mb-20">
            <div className="flex gap-4 overflow-x-auto pb-4 px-2 snap-x snap-mandatory sm:flex-wrap sm:justify-center sm:overflow-visible">
              {APP_SCREENSHOTS.map(({ src, alt }, index) => (
                <button
                  key={alt}
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="shrink-0 snap-center cursor-zoom-in"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="h-80 sm:h-96 w-auto rounded-xl border border-border shadow-lg transition-transform hover:scale-[1.02]"
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-12">
              Why Language Learners Choose Laftel Lingo
            </h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <Zap
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Flashcards in Seconds
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Create and organize flashcards without slowing down. A simple, focused interface keeps
                  your attention on learning, not managing your study tools.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <FileText
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Notes to Flashcards Instantly
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Laftel Lingo turns class notes into ready-to-study flashcards in moments. Spend less
                  time preparing and more time remembering what you learned.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <Brain
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Smart Spaced Repetition
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Review each card at the right time. Laftel Lingo helps you learn faster, remember longer,
                  and focus on the vocabulary that needs your attention.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <Languages
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Instant, Context-Aware Translations
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Look up accurate translations while you study. Add new vocabulary immediately without
                  switching apps or interrupting your flow.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <BookOpenText
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Helpful Generated Examples
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  See new vocabulary used in natural, memorable examples. Laftel Lingo generates them
                  automatically, and you can edit each example to your liking.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <MonitorSmartphone
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Study Anywhere
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Use Laftel Lingo on desktop or mobile and keep your vocabulary with you. Look up words
                  on desktop as you study, then review your cards wherever you go.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <Volume2
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Hear Every Word
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Listen to the pronunciation of new vocabulary as you learn. Hearing each word helps
                  reinforce your memory and builds confidence when speaking.
                </p>
              </div>

              <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border hover:shadow-xl transition-shadow">
                <Download
                  aria-hidden="true"
                  data-testid="feature-icon"
                  className="w-12 h-12 mb-4 text-primary"
                />
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  Export Your Vocabulary Anytime
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Your vocabulary stays yours. Export your Laftel Lingo flashcards easily whenever you
                  want to study, share, or use them somewhere else.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <div className="bg-card rounded-2xl border border-primary/40 shadow-lg p-8 sm:p-12 text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary mb-3">
                Limited Spots
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Join the Beta and Help Shape Laftel Lingo
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto mb-10">
                We&rsquo;re opening up a limited number of spots in our beta program. Join to get
                early access, tell us exactly what you want to see next, and help decide where
                Laftel Lingo goes from here.
              </p>
              <div className="grid gap-8 sm:grid-cols-3 mb-10 text-left">
                <div>
                  <Users aria-hidden="true" className="w-10 h-10 mb-3 text-primary" />
                  <h3 className="text-lg font-bold text-foreground mb-2">Shape the Roadmap</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Request the features you want to see. Beta members send suggestions straight
                    to the team building them.
                  </p>
                </div>
                <div>
                  <Gift aria-hidden="true" className="w-10 h-10 mb-3 text-primary" />
                  <h3 className="text-lg font-bold text-foreground mb-2">6 Months of Premium, Free</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Join the beta and get 6 months of Premium on us, as a thank-you for helping
                    us build.
                  </p>
                </div>
                <div>
                  <Ticket aria-hidden="true" className="w-10 h-10 mb-3 text-primary" />
                  <h3 className="text-lg font-bold text-foreground mb-2">Limited Spots</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We&rsquo;re keeping the beta small so every voice counts. Apply while spots are
                    still open.
                  </p>
                </div>
              </div>
              <Link
                to="/feedback"
                state={{ subject: BETA_APPLICATION_SUBJECT, message: BETA_APPLICATION_MESSAGE }}
                className="inline-block bg-primary text-primary-foreground px-8 py-3 rounded-lg font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
              >
                Apply to Join the Beta
              </Link>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-12">
              Pricing
            </h2>
            <div className="mb-20 grid gap-6 md:grid-cols-2">
              <div className="bg-card rounded-2xl border border-border p-6 sm:p-8">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary mb-3">Free</p>
                <h2 className="text-3xl font-bold text-foreground mb-2">$0</h2>
                <p className="text-muted-foreground mb-6">
                  Free for up to {FREE_FLASHCARD_LIMIT} flashcards.
                </p>
              </div>
              <div className="bg-card rounded-2xl border border-primary/40 p-6 sm:p-8 shadow-lg">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary mb-3">Premium</p>
                <h2 className="text-3xl font-bold text-foreground mb-2">{PREMIUM_PRICE_LABEL}</h2>
                <p className="text-muted-foreground mb-6">
                  Unlimited flashcards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border mt-8">
        <div className="container mx-auto px-4 py-8 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Laftel Lingo logo" className="w-8 h-8" />
            <span className="text-foreground font-semibold text-lg">Laftel Lingo</span>
          </div>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors">
              Terms and Conditions
            </Link>
          </div>
        </div>
      </footer>
      </div>

      {lightboxIndex !== null && (
        <div
          ref={lightboxDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="App screenshot viewer"
          onKeyDown={handleLightboxKeyDown}
          className="fixed inset-0 z-[60] bg-black/95"
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white p-2 rounded-lg"
          >
            <X className="w-8 h-8" />
          </button>
          <ChevronLeft
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 text-white/70"
          />
          <ChevronRight
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 text-white/70"
          />
          <div
            ref={lightboxRef}
            onClick={navigateLightbox}
            className="flex w-full h-full overflow-x-auto snap-x snap-mandatory cursor-pointer"
          >
            {APP_SCREENSHOTS.map(({ src, alt, title, description }) => (
              <div
                key={alt}
                className="w-full h-full flex items-center justify-center shrink-0 snap-center px-12 sm:px-20 py-8"
              >
                <figure className="flex h-full w-[29vh] max-w-full flex-col items-center justify-center gap-6 sm:w-[32vh]">
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-auto min-h-0 rounded-xl border-2 border-white/40 shadow-2xl object-contain"
                  />
                  <figcaption className="w-full shrink-0 rounded-xl border-2 border-white/40 bg-secondary px-4 py-3 text-center shadow-2xl sm:px-6 sm:py-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary">
                      Feature highlight
                    </p>
                    <p className="mt-1 text-lg sm:text-xl font-bold text-secondary-foreground">{title}</p>
                    <p className="mt-1 text-sm sm:text-base leading-relaxed text-secondary-foreground/80">
                      {description}
                    </p>
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
